import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { withManagement } from "../../../shared/hoc/with-management";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import BoardLoading from "./board-loading";
import "./board.scss"; // hub tabs render board components (TypeIcon, BoardAvatar, …) — ensure their styles load
import "./board-project-hub.scss";
import { buildBoardPath, findBoardBySlug } from "./board-routing";
import NewBoardModal from "./new-board-modal";
import ProjectAutomations from "./project-automations";
import ProjectIssues from "./project-issues";
import ProjectReleases from "./project-releases";
import ProjectSettings from "./project-settings";
import ProjectWorkflow from "./project-workflow";
import { Board, Project, boardProjectKeys } from "./types";
import { useBoardConfig } from "./use-board";

type HubTab = "boards" | "issues" | "workflow" | "automations" | "releases" | "settings";

interface HubTabEntry {
  id: HubTab;
  label: string;
}

const TABS: HubTabEntry[] = [
  { id: "boards", label: "Boards" },
  { id: "issues", label: "Issues" },
  { id: "workflow", label: "Workflow" },
  { id: "automations", label: "Automations" },
  { id: "releases", label: "Releases" },
  { id: "settings", label: "Settings" }
];

// Workflow (statuses/transitions) and Settings (rename/delete, custom fields) are management-only.
// Non-managers see them VISIBLE but read-only with a lock glyph (the Releases pattern) — never
// hidden: a vanishing tab reads as "broken", and the surfaces are useful read-only context.
const MANAGE_ONLY_TABS: ReadonlySet<HubTab> = new Set<HubTab>(["workflow", "automations", "settings"]);

interface BoardProjectHubProps {
  domainName: string;
  projectKey: string;
  project: Project;
}

// The per-project work hub (Boards / Issues / Workflow / Releases / Settings). A project is the
// first-class entity issue-tracking hangs off — boards are views under it. Reached at
// /{domain}/board/{projectKey}; the active tab is the ?tab= query param.
const BoardProjectHubInner: React.FC<BoardProjectHubProps> = ({ domainName, projectKey, project }) => {
  const { config } = useBoardConfig();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [newBoardOpen, setNewBoardOpen] = useState<boolean>(false);

  const tabParam: string | null = searchParams.get("tab");
  const tab: HubTab = TABS.some((entry: HubTabEntry) => entry.id === tabParam) ? (tabParam as HubTab) : "boards";
  const setTab = (next: HubTab): void => {
    const sp: URLSearchParams = new URLSearchParams(searchParams);
    if (next === "boards") sp.delete("tab");
    else sp.set("tab", next);
    setSearchParams(sp, { replace: true });
  };

  // The project's boards (single-project boards on this project, plus multi-project boards that
  // include it, shown as "shared").
  const boards: Board[] = useMemo(
    () => config.boards.filter((board: Board) => boardProjectKeys(board).includes(projectKey)),
    [config.boards, projectKey]
  );

  return (
    <div className="kuika-board kuika-project-hub" data-testid="kuika-project-hub">
      <div className="kuika-project-hub__head">
        <button type="button" className="kuika-project-hub__back" onClick={() => navigate(`/${domainName}/board`)}>
          <KuikaSvg name="arrow-left" type="arrows" width={15} height={15} />
          Projects
        </button>
        <div className="kuika-project-hub__title">
          <span className="kuika-project-hub__dot" style={{ background: project.color }} aria-hidden="true" />
          <h2>{project.name}</h2>
          <span className="kuika-project-hub__key">{project.key}</span>
        </div>
      </div>

      <nav className="kuika-project-hub__tabs" aria-label="Project sections">
        {TABS.map((entry: HubTabEntry) => {
          const locked: boolean = MANAGE_ONLY_TABS.has(entry.id) && !config.canManage;
          return (
            <button
              key={entry.id}
              type="button"
              className={`kuika-project-hub__tab${tab === entry.id ? " kuika-project-hub__tab--active" : ""}`}
              onClick={() => setTab(entry.id)}
              title={locked ? "View only — workspace owners or supervisors can edit" : undefined}
            >
              {entry.label}
              {locked && <KuikaSvg name="lock" width={11} height={11} />}
            </button>
          );
        })}
      </nav>

      <div className="kuika-project-hub__body">
        {tab === "boards" && (
          <div className="kuika-project-hub__boards">
            <div className="kuika-project-hub__boards-head">
              <span>Boards over {project.key}</span>
              {config.canManage && (
                <KuikaButton
                  type="secondary"
                  size="sm"
                  onClick={() => setNewBoardOpen(true)}
                  data-testid="project-hub-new-board"
                >
                  <KuikaSvg name="plus" width={14} height={14} />
                  <span style={{ marginLeft: 6 }}>New board</span>
                </KuikaButton>
              )}
            </div>
            {boards.length === 0 ? (
              <div className="kuika-project-hub__empty">No boards yet. Create one to start tracking work.</div>
            ) : (
              <ul className="kuika-project-hub__board-list">
                {boards.map((board: Board) => {
                  const shared: boolean = board.scope.kind === "multi_project";
                  const href: string = buildBoardPath(domainName ?? "", board);
                  return (
                    <li key={board.id}>
                      <button type="button" className="kuika-project-hub__board-row" onClick={() => navigate(href)}>
                        <span className="kuika-project-hub__board-name">{board.name}</span>
                        {shared && <span className="kuika-project-hub__board-tag">shared</span>}
                        <span className="kuika-project-hub__board-mode">{board.mode ?? "scrum"}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {tab === "workflow" && <ProjectWorkflow projectKey={projectKey} />}
        {tab === "automations" && <ProjectAutomations projectKey={projectKey} />}

        {tab === "issues" && <ProjectIssues projectKey={projectKey} />}
        {tab === "releases" && <ProjectReleases projectKey={projectKey} />}
        {tab === "settings" && <ProjectSettings project={project} onDeleted={() => navigate(`/${domainName}/board`)} />}
      </div>

      <NewBoardModal open={newBoardOpen} onClose={() => setNewBoardOpen(false)} projectKey={projectKey} />
    </div>
  );
};

// Resolves the :projectKey route param to a board project. Falls back gracefully: an unknown
// segment that matches a legacy board code redirects to that board's new URL; anything else
// bounces to the projects index.
const BoardProjectHubResolver: React.FC = () => {
  const { config, loaded } = useBoardConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const { domainName, projectKey } = useParams<{ domainName: string; projectKey: string }>();

  const project: Project | undefined = useMemo(
    () => config.projects.find((entry: Project) => entry.key === projectKey),
    [config.projects, projectKey]
  );

  // A single segment that isn't a project key (a board-name slug deep link, or a saved-view href)
  // lands on this route; redirect it to the project-scoped board URL, preserving the query string
  // (jql / filters / view).
  useEffect(() => {
    if (!loaded || project || !projectKey || !domainName) return;
    const board: Board | undefined = findBoardBySlug(config.boards, projectKey);
    if (board) {
      navigate(`${buildBoardPath(domainName, board)}${location.search}${location.hash}`, { replace: true });
    } else {
      navigate(`/${domainName}/board`, { replace: true });
    }
  }, [loaded, project, projectKey, domainName, config.boards, location.search, location.hash, navigate]);

  if (!loaded) {
    return (
      <div className="kuika-board" data-testid="kuika-board">
        <BoardLoading />
      </div>
    );
  }
  if (!project || !projectKey || !domainName) {
    return (
      <div className="kuika-board" data-testid="kuika-board">
        <BoardLoading />
      </div>
    );
  }
  return <BoardProjectHubInner domainName={domainName} projectKey={projectKey} project={project} />;
};

const BoardProjectHub = withManagement<{}>(BoardProjectHubResolver);
// The unwrapped resolver (no management shell) is reused by the in-designer board overlay.
export { BoardProjectHub, BoardProjectHubResolver };
export default BoardProjectHub;
